import { Microfrontend } from '@cian/mf-react';
import { useBooleanState } from '@cian/react-utils';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { NewbuildingBrokerWidgetContext } from './NewbuildingBrokerWidgetContext';
import { useNewbuildingBrokerWidget } from './hooks';
import { trackFallbackModalOpen, trackFallbackModalRetryClick } from './tracking';
import { TOpenWidgetResultHandler } from './types';
import { NewbuildingBrokerFallbackModal } from '../../components/NewbuildingBrokerFallbackModal';
import { selectPage } from '../../selectors';
import { IOpenBrokerModalParams } from '../../types/microfrontends/newbuildingBrokerWidget';
import { IBrokerAnalyticsUser } from '../../types/newbuildingBroker';

export const NewbuildingBrokerWidgetProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const paramsRef = React.useRef<IOpenBrokerModalParams>();
  const [loading, setLoading] = React.useState(false);
  const { state: isFallbackModalVisible, setTrue: showFallbackModal, setFalse: closeFallbackModal } = useBooleanState();
  const page = useSelector(selectPage);

  const handleWidgetOpeningFinish = React.useCallback<TOpenWidgetResultHandler>(
    ({ isError }) => {
      if (isError) {
        const user = paramsRef.current?.trackingParams?.user as IBrokerAnalyticsUser;

        if (!isFallbackModalVisible) {
          trackFallbackModalOpen(user, page.deviceType);
          showFallbackModal();
        }
      } else if (isFallbackModalVisible && !isError) {
        closeFallbackModal();
      }
    },
    [closeFallbackModal, page, isFallbackModalVisible, showFallbackModal],
  );

  const { openWidget, widgetRef, widgetManifest } = useNewbuildingBrokerWidget(handleWidgetOpeningFinish);

  const handleWidgetOpeningStart = React.useCallback(
    async (params: IOpenBrokerModalParams) => {
      // Сохраняем в реф для работы с теми же параметрами на случай, если произойдет ошибка.
      paramsRef.current = params;

      await openWidget(params);
    },
    [openWidget],
  );

  const handleRetryClick = React.useCallback(async () => {
    const user = paramsRef.current?.trackingParams?.user as IBrokerAnalyticsUser;
    trackFallbackModalRetryClick(user, page.deviceType);

    setLoading(true);
    await openWidget(paramsRef.current as IOpenBrokerModalParams);
    setLoading(false);
  }, [page, openWidget]);

  return (
    <NewbuildingBrokerWidgetContext.Provider value={handleWidgetOpeningStart}>
      {children}
      {widgetManifest && (
        <Microfrontend
          ref={widgetRef}
          name={widgetManifest.microfrontendName}
          image={widgetManifest.imageVersion}
          runtime={widgetManifest.runtimeName}
          instance={widgetManifest.instanceId}
        />
      )}
      {isFallbackModalVisible && (
        <NewbuildingBrokerFallbackModal
          isLoading={loading}
          isOpen={isFallbackModalVisible}
          onClose={closeFallbackModal}
          onRetry={handleRetryClick}
        />
      )}
    </NewbuildingBrokerWidgetContext.Provider>
  );
};
